"use client";
import { useState } from "react";
import type { ResolvedToolDefinition, ToolFaq } from "@/data/tools";
import { ChevronDown } from "lucide-react";
import ToolSectionHeader from "./ToolSectionHeader";

type FaqAccordionProps = {
  faqs: readonly ToolFaq[];
};

type ToolFaqSectionProps = {
  tool: ResolvedToolDefinition;
  faqs?: ToolFaq[];
};

export default function ToolFaqSection({ tool, faqs }: ToolFaqSectionProps) {
  const section = tool.pageContent?.sections?.faqs;
  const resolvedFaqs = faqs ?? tool.pageContent?.faqs ?? [];
  const hasSectionCopy = Boolean(section?.title || section?.description);

  if (!resolvedFaqs.length && !hasSectionCopy) {
    return null;
  }

  return (
    <section
      className="max-w-3xl mx-auto space-y-6"
      aria-labelledby="faq-heading"
    >
      <ToolSectionHeader
        headingId="faq-heading"
        title={section?.title ?? "Frequently Asked Questions"}
        description={section?.description}
      >
        <div className="h-1 w-12 bg-[#fb397d] mx-auto rounded" />
      </ToolSectionHeader>
      {resolvedFaqs.length ? (
        <FaqAccordion faqs={resolvedFaqs} />
      ) : (
        <div className="rounded-2xl border border-dashed border-[#f2bfd3] bg-gradient-to-br from-white to-[#fff7fb] p-6 text-center text-sm text-gray-500">
          No FAQ items are configured for this tool yet, but the FAQ intro copy
          is still shown.
        </div>
      )}
    </section>
  );
}

function FaqAccordion({ faqs }: FaqAccordionProps) {
  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <FaqItem key={faq.question} index={index} {...faq} />
      ))}
    </div>
  );
}

function FaqItem({ question, answer, index }: ToolFaq & { index: number }) {
  const [open, setOpen] = useState(false);
  const accentClasses =
    index % 2 === 0
      ? {
          open: "border-[#5b32b4]/40 bg-gradient-to-br from-[#faf7ff] to-white shadow-md shadow-[#5b32b4]/10",
          closed: "border-[#e9e1ff] bg-white hover:border-[#d4c5f9]",
          badgeOpen: "bg-gradient-to-br from-[#5b32b4] to-[#fb397d] text-white",
          badgeClosed: "bg-[#f3ecff] text-[#5b32b4]",
        }
      : {
          open: "border-[#fb397d]/30 bg-gradient-to-br from-[#fff7fb] to-white shadow-md shadow-[#fb397d]/10",
          closed: "border-[#f4d7e6] bg-white hover:border-[#f2bfd3]",
          badgeOpen: "bg-gradient-to-br from-[#fb397d] to-[#ff8a5b] text-white",
          badgeClosed: "bg-[#fff0f6] text-[#c3003a]",
        };

  return (
    <div
      className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
        open ? accentClasses.open : accentClasses.closed
      }`}
    >
      <button
        type="button"
        aria-label="Expand"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <div className="flex items-center gap-4">
          <span
            className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all ${
              open ? accentClasses.badgeOpen : accentClasses.badgeClosed
            }`}
          >
            {index + 1}
          </span>
          <span className="font-bold text-[#2b1d3a] text-sm sm:text-base">
            {question}
          </span>
        </div>
        <ChevronDown
          size={18}
          className={`shrink-0 text-[#5b32b4] transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <div
        className={`grid transition-all duration-300 ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-5 text-gray-600 text-sm leading-relaxed pl-[4.5rem]">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}
